import React, { useEffect, useState } from 'react';
import { analyticsService, DashboardAnalytics, EnhancedAnalytics } from '../../services/analyticsService';
import {
  Box,
  Chip,
  Stack,
  Skeleton,
  Badge
} from '@mui/material';

interface BadgeProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  icon?: string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

const CategoryBadge: React.FC<BadgeProps> = ({ 
  label, 
  count, 
  active, 
  onClick, 
  icon, 
  color = 'default' 
}) => (
  <Chip
    label={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <span>{icon}</span>}
        <span>{label}</span>
        <Badge 
          badgeContent={count} 
          color={active ? 'primary' : 'default'}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: active ? 'primary.main' : 'grey.400',
              color: 'white',
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16
            }
          }}
        >
          <Box sx={{ width: 0 }} />
        </Badge>
      </Box>
    }
    onClick={onClick}
    variant={active ? 'filled' : 'outlined'}
    color={active ? 'primary' : 'default'}
    sx={{
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: 2
      }
    }}
  />
);

interface CategoryBadgesProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}

const CategoryBadges: React.FC<CategoryBadgesProps> = ({ 
  activeCategory, 
  onCategoryChange 
}) => {
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await analyticsService.getDashboardAnalytics();
        setAnalytics(data);
      } catch (error) {
        console.error('Failed to load analytics for badges:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();

    // Refresh every 10 seconds for faster updates
    const interval = setInterval(fetchAnalytics, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton 
            key={i} 
            variant="rounded" 
            width={120} 
            height={32} 
            sx={{ borderRadius: 4 }} 
          />
        ))}
      </Stack>
    );
  }

  if (!analytics) {
    return null;
  }

  const badges = [
    { 
      key: 'all', 
      label: 'All Bookings', 
      count: analytics.categories.all, 
      icon: '📊',
      color: 'primary' as const
    },
    { 
      key: 'self-drive', 
      label: 'Self-Drive', 
      count: analytics.categories['self-drive'], 
      icon: '🚗',
      color: 'success' as const
    },
    { 
      key: 'with-driver', 
      label: 'With Driver', 
      count: analytics.categories['with-driver'], 
      icon: '🤵',
      color: 'info' as const
    },
    { 
      key: 'driver-hire', 
      label: 'Driver Hire', 
      count: analytics.categories['driver-hire'], 
      icon: '👨‍✈️',
      color: 'warning' as const
    },
    { 
      key: 'yacht-charter', 
      label: 'Yacht Bookings', 
      count: analytics.categories['yacht-charter'], 
      icon: '🛥️',
      color: 'secondary' as const
    },
    { 
      key: 'hybrid', 
      label: 'Hybrid', 
      count: analytics.categories.hybrid, 
      icon: '🔄',
      color: 'info' as const
    },
    { 
      key: 'instant', 
      label: 'Instant', 
      count: analytics.bookingTypes.instant, 
      icon: '⚡',
      color: 'warning' as const
    },
  ];

  return (
    <Stack 
      direction="row" 
      spacing={2} 
      sx={{ 
        mb: 3, 
        flexWrap: 'wrap',
        gap: 1
      }}
    >
      {badges.map(badge => (
        <CategoryBadge
          key={badge.key}
          label={badge.label}
          count={badge.count}
          active={activeCategory === badge.key}
          onClick={() => onCategoryChange(badge.key)}
          icon={badge.icon}
          color={badge.color}
        />
      ))}
    </Stack>
  );
};

export default CategoryBadges;